// Offline-meldingskø (store-and-forward) med lesebekreftelse. Ved brutt/lavbånds
// samband holdes utgående meldinger lokalt og sendes på nytt med backoff til de
// er levert; mottakeren kvitterer «levert» og «lest» (som i feltappen). Ren,
// deterministisk motor (ingen skjult tid — `nowMs` injiseres) → fullt testbar.
// Persisteres via export()/import(); wires til hub/WS i klient + backend.
import { z } from 'zod';
export const ReceiptStatus = z.enum(['pending', 'sent', 'delivered', 'read', 'failed']);
export const OutboxMessageSchema = z.object({
    id: z.string().min(1).max(64),
    to: z.string().max(200),
    kind: z.string().max(48).default('chat'),
    body: z.unknown(),
    createdMs: z.number(),
    status: ReceiptStatus.default('pending'),
    attempts: z.number().int().min(0).default(0),
    lastAttemptMs: z.number().optional(),
    deliveredMs: z.number().optional(),
    readMs: z.number().optional(),
});
export class Outbox {
    items = new Map();
    baseMs;
    maxMs;
    factor;
    maxAttempts;
    constructor(opts = {}) {
        this.baseMs = opts.baseMs ?? 5_000;
        this.maxMs = opts.maxMs ?? 300_000;
        this.factor = opts.factor ?? 2;
        this.maxAttempts = opts.maxAttempts ?? 8;
    }
    /** Legg en melding i kø (status 'pending'). Idempotent på id. */
    enqueue(msg) {
        const m = OutboxMessageSchema.parse({ ...msg, status: 'pending', attempts: 0 });
        this.items.set(m.id, m);
        return m;
    }
    /** Backoff-forsinkelse for et gitt forsøk (eksponentiell, capped). */
    backoff(attempts) {
        return Math.min(this.maxMs, this.baseMs * this.factor ** Math.max(0, attempts - 1));
    }
    /** Er meldingen klar for (re)sending nå? */
    ready(m, nowMs) {
        if (m.status === 'delivered' || m.status === 'read' || m.status === 'failed')
            return false;
        if (m.status === 'pending' && m.attempts === 0)
            return true;
        if (m.lastAttemptMs === undefined)
            return true;
        return nowMs - m.lastAttemptMs >= this.backoff(m.attempts);
    }
    /**
     * Neste batch å sende nå (opptil `maxN`, eldste først). Markerer dem 'sent' +
     * øker forsøkstelleren; når maxAttempts er nådd settes 'failed' i stedet.
     */
    nextBatch(nowMs, maxN = 50) {
        const ready = [...this.items.values()].filter((m) => this.ready(m, nowMs)).sort((a, b) => a.createdMs - b.createdMs).slice(0, maxN);
        const out = [];
        for (const m of ready) {
            if (m.attempts >= this.maxAttempts) {
                m.status = 'failed';
                continue;
            }
            m.attempts += 1;
            m.lastAttemptMs = nowMs;
            m.status = 'sent';
            out.push(m);
        }
        return out;
    }
    /** Registrer kvittering fra mottaker: 'delivered' eller 'read'. */
    ack(id, kind, nowMs) {
        const m = this.items.get(id);
        if (!m)
            return false;
        if (kind === 'delivered') {
            if (m.status !== 'read')
                m.status = 'delivered';
            m.deliveredMs = m.deliveredMs ?? nowMs;
        }
        else {
            m.status = 'read';
            m.readMs = m.readMs ?? nowMs;
            if (!m.deliveredMs)
                m.deliveredMs = nowMs;
        }
        return true;
    }
    get(id) { return this.items.get(id); }
    pending() { return [...this.items.values()].filter((m) => m.status === 'pending' || m.status === 'sent'); }
    failed() { return [...this.items.values()].filter((m) => m.status === 'failed'); }
    all() { return [...this.items.values()]; }
    /** Rydd bekreftede/feilede meldinger eldre enn `maxAgeMs` (mot ubegrenset vekst). */
    prune(nowMs, maxAgeMs = 24 * 3_600_000) {
        let n = 0;
        for (const [id, m] of this.items) {
            const terminal = m.status === 'read' || m.status === 'failed';
            if (terminal && nowMs - m.createdMs > maxAgeMs) {
                this.items.delete(id);
                n++;
            }
        }
        return n;
    }
    export() { return this.all(); }
    import(items) {
        for (const raw of items) {
            const r = OutboxMessageSchema.safeParse(raw);
            if (r.success)
                this.items.set(r.data.id, r.data);
        }
    }
}
//# sourceMappingURL=outbox.js.map